import React from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@mui/material';
import { useClientEmail, useClientId, useClientPhoneNumber } from '@/hooks/client';
import { RQQuery } from '@/types/query';
import { Client } from '@/types/client';
import { ErrorView, ServiceErrorView } from '@/components/atoms/Error';
import BasicInfo from '@/components/organisms/client/BasicInfo';
import ContactInfo from '@/components/organisms/client/ContactInfo';
import Shipping from '@/components/organisms/client/Shipping';
import StatusPermission from '@/components/organisms/client/StatusPermission';
import TalonOnePromoCode from '@/components/organisms/client/TalonOnePromoCode';
import PromoCode from '@/components/organisms/client/PromoCode';
import Tradesy from '@/components/organisms/client/Tradesy';
import Fraud from '@/components/organisms/client/Fraud';
import ActionLog from '../item/ActionLog';
import GetPaid from './GetPaid';
import DsaReports from '@/components/organisms/common/DsaReports';
import ExtraPayout from './ExtraPayout';
import KycTable from './Kyc';

export const ClientEmailView: React.FC<{ email: string; isBuyer: boolean; showKycBadge?: boolean }> = ({
  email,
  isBuyer,
  showKycBadge = true,
}) => {
  const data = useClientEmail(email);

  return (
    <View
      data={data}
      isBuyer={isBuyer}
      showKycBadge={showKycBadge}
    />
  );
};

export const ClientIdView: React.FC<{ id: number; isBuyer: boolean; showKycBadge?: boolean }> = ({
  id,
  isBuyer,
  showKycBadge = true,
}) => {
  const data = useClientId(id);

  return (
    <View
      data={data}
      isBuyer={isBuyer}
      showKycBadge={showKycBadge}
    />
  );
};

export const ClientPhoneNumberView: React.FC<{ phoneNumber: string; isBuyer: boolean; showKycBadge?: boolean }> = ({
  phoneNumber,
  isBuyer,
  showKycBadge = true,
}) => {
  const data = useClientPhoneNumber(phoneNumber);

  return (
    <View
      data={data}
      isBuyer={isBuyer}
      showKycBadge={showKycBadge}
    />
  );
};

const View: React.FC<{
  data: RQQuery<Client[]>;
  isBuyer: boolean;
  showKycBadge: boolean;
}> = ({ data, isBuyer, showKycBadge }) => {
  const { t } = useTranslation();
  return (
    <>
      {data.kind === 'Called' && (
        <Skeleton
          height={100}
          variant="rounded"
        />
      )}
      {data.kind === 'Error' && <ServiceErrorView error={data.error} />}
      {data.kind === 'Loaded' && data.data.length === 0 && <ErrorView message={t('client.not_found')} />}
      {data.kind === 'Loaded' && data.data.length > 0 && (
        <>
          <BasicInfo
            client={data.data[0]}
            isBuyer={isBuyer}
            showKycBadge={showKycBadge}
          />
          <ContactInfo client={data.data[0]} />
          <KycTable client={data.data[0]} />
          <Fraud client={data.data[0]} />
          <GetPaid client={data.data[0]} />
          <ExtraPayout
            client={data.data[0]}
            isBuyer={isBuyer}
          />
          <Shipping client={data.data[0]} />
          <StatusPermission client={data.data[0]} />
          <TalonOnePromoCode client={data.data[0]} />
          <PromoCode client={data.data[0]} />
          <DsaReports clientId={Number(data.data[0].clientId)} />
          <Tradesy client={data.data[0]} />
          <ActionLog
            id={Number(data.data[0].clientId)}
            type="customer"
          />
        </>
      )}
    </>
  );
};
